// @flow
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import { FormInput } from '../components';
import { addMasterAPI, getAllVendorsAPI, getUserStationAPI, exportMasterAPI } from '../API/RDPMS_API';
import { Loading } from 'react-loading-ui';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'material-design-icons/iconfont/material-icons.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './dashboard/Dashboard/Dashboard.css';
import '../../src/assets/scss/custom/components/reportalert.scss';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { capitalizeFirstLetter } from '../constants/Uppercase';
import { Card, Col, Row } from 'react-bootstrap';
import Table from '../components/Table';
import { masterColumns } from '../utils/ColumnUtils';

const MasterImport = () => {
    const fileInputRef = useRef(null);
    const currentYear = new Date().getFullYear();
    const { t } = useTranslation();
    const [station, setStation] = useState([]);
    const [vendor, setVendor] = useState([]);
    const [selectedStation, setSelectedStation] = useState('');
    const [excelData, setExcelData] = useState([]);

    const { auth } = useSelector((state) => ({
        auth: state.Auth,
    }));

    const userRole = capitalizeFirstLetter(auth?.user?.role || 'Admin');

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        await Promise.all([fetchStations(), fetchVendors()]);
    };

    const fetchStations = async () => {
        try {
            if (!auth?.user?._id) return;
            Loading(true);
            const response = await getUserStationAPI(auth.user._id);
            if (Array.isArray(response) && response.length > 0) setStation(response);
        } catch (error) {
            console.error('Error fetching station data:', error);
            toast.error(t('Failed to fetch station data'), { position: 'top-right' });
        } finally {
            Loading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const response = await getAllVendorsAPI();
            if (Array.isArray(response) && response.length) setVendor(response);
        } catch (error) {
            console.error('Vendor fetch error:', error);
            toast.error(t('Failed to fetch vendor data'), { position: 'top-right' });
        }
    };

    const handleImport = async () => {
        try {
            if (!selectedStation) {
                toast.error(t('Please select station and vendor'), { position: 'top-right' });
                return;
            }

            const response = await addMasterAPI(excelData);

            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }

            if (response?.isSucess === true) {
                toast.success(t('Data imported successfully'), { position: 'top-right' });
            }
        } catch (error) {
            console.error('Import error:', error);
            toast.error(t('Failed to import data'), { position: 'top-right' });
        }
    };

    const handleFileImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!selectedStation) {
            toast.error('Please select Station first', { position: 'top-right' });
            e.target.value = null;
            return;
        }

        const reader = new FileReader();
        reader.onload = (evt) => {
            const data = evt.target.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            const mappedData = jsonData.map((row) => {
                const assetType = row.asset_type_name.toLowerCase().replace(/\s+/g, '');
                let newRow = { ...row };

                // DC Track Circuit
                if (assetType.includes('dc')) {
                    newRow.track_number = row.name;
                    newRow.model = 'MasterDcCircuit';
                    delete newRow.name;

                    // Point Machine
                } else if (assetType.includes('pointmachine')) {
                    newRow.point_machine = row.name;
                    newRow.model = 'MasterPointMachine';
                    delete newRow.name;

                    // Signal
                } else if (assetType.includes('signal')) {
                    newRow.signal_number = row.name;
                    newRow.model = 'MasterSignal';
                    delete newRow.name;

                    // Battery Charger
                } else if (assetType.includes('batterycharger')) {
                    newRow.battery_charger = row.name;
                    newRow.model = 'MasterBatteryCharger';
                    delete newRow.name;

                    // IPS
                } else if (assetType.includes('ips')) {
                    newRow.ips = row.name;
                    newRow.model = 'MasterIPSOverview';
                    delete newRow.name;

                    // HASSDAC
                } else if (assetType.includes('hassdac')) {
                    newRow.hassdac = row.name;
                    newRow.model = 'MasterHASSDAC';
                    delete newRow.name;
                }

                // Station & User
                newRow.station_id = selectedStation;
                newRow.created_by = userRole;
                newRow.updated_by = userRole;

                // Vendor
                const vendorNameFromExcel = row.vendor_name || '';
                if (vendorNameFromExcel) {
                    const matchedVendor = vendor.find(
                        (v) =>
                            v.vendor_cloud_name &&
                            v.vendor_cloud_name.toLowerCase() === vendorNameFromExcel.toLowerCase()
                    );
                    if (matchedVendor) {
                        newRow.vendor_name = vendorNameFromExcel;
                        newRow.vendor_id = matchedVendor._id;
                    } else {
                        newRow.vendor_name = vendorNameFromExcel;
                        newRow.vendor_id = '';
                    }
                } else {
                    newRow.vendor_name = '';
                    newRow.vendor_id = '';
                }

                return newRow;
            });

            // console.log(mappedData, 'Mapped Excel Data');
            setExcelData(mappedData);
        };

        reader.readAsBinaryString(file);
    };
    const handleExport = async () => {
        try {
            if (!selectedStation) {
                toast.error('Please select Station');
                return;
            }

            Loading(true);

            const response = await exportMasterAPI({
                station_id: selectedStation,
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'importMaster.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success('Exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Export failed');
        } finally {
            Loading(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <div className="container-fluid px-2">
                <div className="row align-items-center gx-2 gy-2 mb-3">
                    <div className="col-12 col-md-auto">
                        <h6 className="point-title fw-bold mb-0">{t('Import Master')}</h6>
                    </div>

                    <div className="col-12 col-md-auto ms-md-auto">
                        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2">
                            <button
                                className="update d-flex align-items-center justify-content-center flex-grow-1"
                                onClick={handleExport}>
                                {t('Export')} <i className="fas fa-file-excel ms-1"></i>
                            </button>
                            <button
                                className="update d-flex align-items-center justify-content-center flex-grow-1"
                                onClick={() => window.location.reload()}>
                                {t('Update')} <i className="fas fa-sync-alt ms-1"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    className="report-alert-card my-4 p-3"
                    style={{
                        width: '100%',
                        boxShadow: '0px 0px 35px rgba(154, 161, 171, 0.15)',
                        position: 'relative',
                    }}>
                    <form className="container form-container justify-content-center">
                        <div className="row d-flex flex-center">
                            <div className="col-md-3 form-group">
                                <label htmlFor="stationSelect">{t('Station')} *</label>
                                <FormInput
                                    name="stationSelect"
                                    type="select"
                                    className="form-select"
                                    id="stationSelect"
                                    onChange={(e) => setSelectedStation(e.target.value)}>
                                    <option value="">{t('Select Station')}</option>
                                    {station.map((item, index) => (
                                        <option key={item._id} value={item._id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </FormInput>
                            </div>

                            <div className="col-md-3 form-group">
                                <label>{t('Import Excel')} *</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileImport}
                                    ref={fileInputRef}
                                />
                            </div>
                        </div>

                        <div className="row mt-4">
                            <div className="col-md-12 text-end">
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={handleImport}
                                    style={{
                                        backgroundColor: '#F9B74C',
                                        color: '#000',
                                        fontWeight: 'bold',
                                        width: '150px',
                                        textTransform: 'uppercase',
                                        fontFamily: "'Poppins', sans-serif",
                                        fontSize: '14px',
                                    }}>
                                    {t('Submit')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {excelData.length > 0 && (
                    <Row>
                        <Col>
                            <Card>
                                <Card.Header as="h4">{t('Master Data')}</Card.Header>
                                <Card.Body>
                                    <Table
                                        columns={masterColumns(t)}
                                        data={excelData}
                                        isSortable={true}
                                        isSearchable={true}
                                    />
                                </Card.Body>
                                <Card.Footer>
                                    Copyright {currentYear} Hummingbird TekSystems, Inc.| CONFIDENTIAL
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </>
    );
};

export default MasterImport;
